/** One error shape for every A0 call, whatever the backend or the transport threw. */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly detail: string
  /** Field path (e.g. `platforms.0`) → message, from a 422 validation list. */
  readonly fields: Record<string, string>
  /** Any other keys of a flat `{code, detail, ...}` body (e.g. `job_status`). */
  readonly extra: Record<string, unknown>

  constructor(init: { status: number; code: string; detail: string; fields?: Record<string, string>; extra?: Record<string, unknown> }) {
    super(init.detail)
    this.name = 'ApiError'
    this.status = init.status
    this.code = init.code
    this.detail = init.detail
    this.fields = init.fields ?? {}
    this.extra = init.extra ?? {}
  }
}

interface TransportFailure {
  name?: string
  code?: string
  message?: string
  response?: { status: number; data?: unknown }
}

interface ValidationItem {
  loc?: Array<string | number>
  msg?: string
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v)

function fromBody(status: number, data: unknown): ApiError {
  if (isRecord(data) && typeof data.code === 'string') {
    const { code, detail, ...extra } = data
    return new ApiError({ status, code, detail: typeof detail === 'string' ? detail : code, extra })
  }
  const detail = isRecord(data) ? data.detail : undefined
  if (isRecord(detail) && typeof detail.code === 'string') return fromBody(status, detail)
  if (Array.isArray(detail)) {
    const fields: Record<string, string> = {}
    for (const item of detail as ValidationItem[]) {
      const loc = (item.loc ?? []).filter((part, i) => !(i === 0 && ['body', 'query', 'path', 'header'].includes(String(part))))
      fields[loc.join('.') || '_'] = item.msg ?? 'Invalid value'
    }
    const first = Object.values(fields)[0] ?? 'Invalid request'
    return new ApiError({ status, code: 'validation_error', detail: first, fields })
  }
  // A proxy error page (Cloudflare 52x sends an RFC 9457 body with `title`, Apache sends HTML) is
  // never shown word for word: the user gets one calm sentence instead of "The origin web server…".
  if (status >= 500 && (!isRecord(data) || typeof data.title === 'string')) {
    return new ApiError({ status, code: `http_${status}`, detail: SERVER_BUSY })
  }
  return new ApiError({ status, code: `http_${status}`, detail: typeof detail === 'string' ? detail : `Request failed (${status})` })
}

export const SERVER_BUSY = 'ZUPA’s server did not answer this time. Please try again in a minute.'

export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error
  const e = (isRecord(error) ? error : {}) as TransportFailure
  if (e.name === 'AbortError' || e.name === 'CanceledError' || e.code === 'ERR_CANCELED') {
    return new ApiError({ status: 0, code: 'aborted', detail: 'The request was cancelled.' })
  }
  if (e.response) return fromBody(e.response.status, e.response.data)
  if (e.code === 'ECONNABORTED' || e.code === 'ETIMEDOUT') {
    return new ApiError({ status: 0, code: 'timeout', detail: 'The server took too long to answer.' })
  }
  if (e.code === 'ERR_NETWORK' || e.message === 'Network Error') {
    return new ApiError({ status: 0, code: 'network_error', detail: 'Could not reach the server.' })
  }
  return new ApiError({ status: 0, code: 'unknown_error', detail: e.message || 'Something went wrong.' })
}

export const isAborted = (error: unknown): boolean => toApiError(error).code === 'aborted'
